import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { navigate } from 'gatsby';
import {
  returnCurrencyCharFromCode,
  returnLocalisedPath,
  returnShopifyVariant,
} from '../../../../utils/globals';
import { ShopProductItemMiniCard } from '../../../UI/Cards/Shop/ProductItemMini/shopProductItemMiniCard';
import { ShopProductItemCard } from '../../../UI/Cards/Shop/ProductItem/shopProductItemCard';
import { IShopItem } from '../../../../utils/types/contentful';
import { pushProductClickToDataLayer } from '../../../../utils/dataLayer/productClick';
import { collectionFiltersState } from '../../../../utils/recoil/collection';
import { returnProductSlugUrl } from './shopProductItem.utils';
import { withLocale } from '../../../../utils/hocs';

export interface ShopItemProps extends IShopItem {
  isMini: boolean;
  targetDiopter?: string;
}

export const ShopProductItem = withLocale(
  ({
    badges,
    buttons,
    defaultProductVariant,
    detaultProductVariantIndex = 0,
    hasQuickBuy,
    isMini,
    label,
    product,
    showButton,
    showColorPicker,
    subLabel,
    locale,
    hasCategoryBadge,
    targetDiopter,
  }) => {
    const infos = useRef({});
    const [selectedVariant, set] = useState(defaultProductVariant);
    const setSelectedVariant = (v) => {
      set(v);
      infos.current = {
        ...infos.current,
        selectedVariant: v,
      };
    };

    const collectionFilters = useRecoilValue(collectionFiltersState);

    const shopifyProduct = product?.fields?.shopifyData || product?.shopifyData;
    const selectedSize = selectedVariant?.size;

    const variantColors = useMemo(() => {
      let variants = product?.variants;
      if (variants) {
        if (selectedVariant?.size) {
          variants = variants.filter(
            (variant) => selectedVariant?.size.value === variant.size?.value
          );
        }

        // Filter by colors
        if (collectionFilters.colors && collectionFilters.colors.length > 0) {
          variants = variants.filter((variant) =>
            collectionFilters.colors?.includes(
              variant.color?.shopifyKey?.toLowerCase()
            )
          );
        }

        // Filter variant with promotion filter
        if (
          collectionFilters.promotion &&
          collectionFilters.promotion.length > 0
        ) {
          variants = variants.filter((variant) =>
            collectionFilters.promotion?.includes(
              variant.promotion?.discount?.toLowerCase()
            )
          );
        }

        if (collectionFilters.colors && collectionFilters.colors.length > 0) {
          variants = variants.filter((variant) =>
            collectionFilters.colors?.includes(
              variant.color?.shopifyKey?.toLowerCase()
            )
          );
        }
      }

      infos.current = { ...infos.current, variantColors: variants };
      return variants;
    }, [product, selectedVariant, collectionFilters?.colors]);

    useEffect(() => {
      // Filter variant with promotion filter
      if (
        ((collectionFilters.colors && collectionFilters.colors.length > 0) ||
          (collectionFilters.promotion &&
            collectionFilters.promotion.length > 0)) &&
        variantColors &&
        variantColors.length > 0
      ) {
        setSelectedVariant(variantColors[0]);
      } else if (
        variantColors?.find(
          (v) =>
            v?.color?.shopifyKey === defaultProductVariant?.color?.shopifyKey
        )
      ) {
        setSelectedVariant(defaultProductVariant);
      } else if (variantColors?.[detaultProductVariantIndex]) {
        setSelectedVariant(variantColors?.[detaultProductVariantIndex]);
      }
    }, [collectionFilters?.colors]);

    const shopifyVariant = returnShopifyVariant(
      shopifyProduct,
      selectedVariant
    );

    const price = shopifyVariant[0]?.priceV2.amount;
    const compareAtPrice = shopifyVariant[0]?.compareAtPriceV2?.amount;
    const currency = returnCurrencyCharFromCode(
      shopifyVariant[0]?.priceV2.currencyCode
    );

    const handleButtonClick = () => {
      // Datalayer => product click
      pushProductClickToDataLayer(product);
      // Navigate to the product page
      navigate(
        returnLocalisedPath(
          locale[0],
          returnProductSlugUrl(product.slug, selectedVariant, targetDiopter)
        )
      );
    };

    if (isMini) {
      return (
        <ShopProductItemMiniCard
          colorVariants={variantColors}
          selectedSize={selectedSize}
          selectedVariant={selectedVariant}
          label={label}
          product={product}
          onClick={handleButtonClick}
        />
      );
    }

    return (
      <ShopProductItemCard
        hasQuickBuy={hasQuickBuy}
        label={label}
        badges={badges}
        currency={currency}
        price={price}
        subLabel={subLabel}
        product={product}
        selectedSize={selectedSize}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        showButton={showButton}
        showColorPicker={showColorPicker}
        hasCategoryBadge={hasCategoryBadge}
        colorVariants={variantColors}
        compareAtPrice={compareAtPrice}
        button={buttons && buttons[0]}
        onClick={handleButtonClick}
      />
    );
  }
);

export const LoadingShopItem = () => (
  <div className='p-3'>
    <div className='w-full animate-pulse bg-gray-200 h-60 loading' />
    <div className='w-full animate-pulse bg-gray-200 h-16 loading mt-4' />
  </div>
);
